import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  SmartToy as AIIcon,
  RocketLaunch as LaunchIcon,
  PlayArrow as RunIcon,
  Analytics as AnalyzeIcon,
  Security as GateIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API } from '../lib/api';
import { normalizeToolId } from '../state/studioStore';
import Panel from './Panel';

const TOOL_ASSISTANT_COPY = {
  live: {
    label: 'Live Trial',
    prompt: 'Stream suites, monitor results, and react in real time.',
  },
  diff: {
    label: 'Diff',
    prompt: 'Compare prompt versions to understand behavioural changes.',
  },
  slices: {
    label: 'Slice Analysis',
    prompt: 'Explore performance cohorts and uncover hidden failure patterns.',
  },
  studio: {
    label: 'RAG Studio',
    prompt: 'Tune retrieval strategies and evaluate knowledge-grounded responses.',
  },
  gate: {
    label: 'Prompt Ops',
    prompt: 'Manage quality gates, approvals, and go/no-go workflows.',
  },
  flow: {
    label: 'Workflow Guide',
    prompt: 'Follow guided steps to move from ideation to production readiness.',
  },
};

function generateExpertInsights(promptCount, runCount, latestRun, recentResults) {
  const passRate = calculatePassRate(recentResults);
  const avgLatency = calculateAverageLatency(recentResults);
  const failurePatterns = identifyFailurePatterns(recentResults);

  const insights = [];
  let actionType = '';

  if (passRate < 0.7) {
    insights.push(`${failurePatterns.length} failure patterns detected in recent tests`);
    actionType = 'analyze_failures';
  } else if (passRate > 0.9) {
    insights.push(`Excellent performance: ${(passRate * 100).toFixed(1)}% pass rate`);
    actionType = 'set_gates';
  } else {
    insights.push(`Good performance: ${(passRate * 100).toFixed(1)}% pass rate with optimization opportunities`);
    actionType = 'optimize';
  }

  if (avgLatency > 5000) {
    insights.push(`High latency detected: ${avgLatency}ms average response time`);
  } else if (avgLatency > 2000) {
    insights.push(`Moderate latency: ${avgLatency}ms - consider optimization`);
  }

  const message = `Workspace Status: ${promptCount} prompts, ${runCount} runs completed

Key Findings:
${insights.map((item) => `• ${item}`).join('\n')}`;

  return { message, actionType };
}

function generateIntermediateInsights(promptCount, suiteCount, runCount, latestRun) {
  const message = `Workspace Status: ${promptCount} prompts, ${runCount} test runs

You have solid testing momentum. Latest run: ${latestRun?.name || 'Unknown'}

Next recommended action: Analyze your test patterns to identify optimization opportunities.`;

  return { message, actionType: 'intermediate' };
}

function generateBeginnerInsights(promptCount, suiteCount, runCount, latestRun) {
  const message = `Workspace Status: ${promptCount} prompts, ${runCount} test runs

Good start with testing! Latest run: ${latestRun?.name || 'Unknown'}

Recommendation: Run more tests to build a reliable performance dataset.`;

  return { message, actionType: 'run_tests' };
}

function generateReadyToTestInsights(promptCount, suiteCount) {
  const message = `Workspace Status: ${promptCount} prompts, ${suiteCount} test suites

Setup complete! Ready to run your first test and see how your prompts perform.`;

  return { message, actionType: 'ready_test' };
}

function generateNeedsSuitesInsights(promptCount) {
  const message = `Workspace Status: ${promptCount} prompts created

Missing: Test suites needed to evaluate prompt performance.`;

  return { message, actionType: 'create_suite' };
}

function generateWelcomeInsights() {
  const message = `Welcome to your AI Prompt Workshop

Ready to create your first AI prompt?`;

  return { message, actionType: 'welcome' };
}

function calculatePassRate(results) {
  if (!results || results.length === 0) return 0;
  const passed = results.filter((r) => r.status === 'pass' || r.success).length;
  return passed / results.length;
}

function calculateAverageLatency(results) {
  if (!results || results.length === 0) return 0;
  const latencies = results
    .map((r) => r.latency_ms || r.duration || 0)
    .filter((latency) => latency > 0);
  return latencies.length > 0 ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length) : 0;
}

function identifyFailurePatterns(results) {
  if (!results || results.length === 0) return [];

  const failures = results.filter((r) => r.status === 'fail' || !r.success);
  const errorGroups = failures.reduce((acc, failure) => {
    const errorKey = failure.error_type || failure.failure_reason || 'Unknown';
    acc[errorKey] = (acc[errorKey] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(errorGroups)
    .filter(([, count]) => count >= 2)
    .map(([error, count]) => ({ error, count }));
}




export default function ContextHistoryPanel({ activeTool, activeCards = [], workspaceSummary }) {


  const navigate = useNavigate();
  const [messages, setMessages] = React.useState([]);
  const [inputMessage, setInputMessage] = React.useState('');
  const [isTyping, setIsTyping] = React.useState(false);
  const [isInitializing, setIsInitializing] = React.useState(false);
  const messagesEndRef = React.useRef(null);
  const summary = React.useMemo(() => workspaceSummary || {}, [workspaceSummary]);
  const promptCount = summary.promptCount ?? (Array.isArray(summary.prompts) ? summary.prompts.length : 0);
  const suiteCount = summary.suiteCount ?? (Array.isArray(summary.suites) ? summary.suites.length : 0);
  const runCount = summary.runCount ?? (Array.isArray(summary.recentRuns) ? summary.recentRuns.length : 0);
  const normalizedActiveTool = normalizeToolId(activeTool || 'live');
  const assistantMeta = TOOL_ASSISTANT_COPY[normalizedActiveTool] || TOOL_ASSISTANT_COPY.live;
  const lastAnnouncedToolRef = React.useRef(normalizedActiveTool);
  const hasBootstrappedRef = React.useRef(false);
  const additionalActiveTools = React.useMemo(() => {
    return (Array.isArray(activeCards) ? activeCards : [])
      .map((id) => normalizeToolId(id))
      .filter((id) => id && id !== normalizedActiveTool);
  }, [activeCards, normalizedActiveTool]);

  // Move analyzeWorkspaceData above all hooks and callbacks that use it
  const analyzeWorkspaceData = React.useCallback((prompts, suites, runs, latestRun, recentResults) => {
    const promptCountLocal = prompts.length;
    const suiteCountLocal = suites.length;
    const runCountLocal = runs.length;

    if (runCountLocal >= 10 && recentResults.length > 0) {
      return generateExpertInsights(promptCountLocal, runCountLocal, latestRun, recentResults);
    } else if (runCountLocal >= 3) {
      return generateIntermediateInsights(promptCountLocal, suiteCountLocal, runCountLocal, latestRun);
    } else if (runCountLocal > 0) {
      return generateBeginnerInsights(promptCountLocal, suiteCountLocal, runCountLocal, latestRun);
    } else if (suiteCountLocal > 0) {
      return generateReadyToTestInsights(promptCountLocal, suiteCountLocal);
    } else if (promptCountLocal > 0) {
      return generateNeedsSuitesInsights(promptCountLocal);
    } else {
      return generateWelcomeInsights();
    }
  }, []);

  const addMessage = React.useCallback((content, isUser, actions = [], isLoading = false) => {
    const newMessage = {
      id: Date.now(),
      content,
      isUser,
      timestamp: new Date().toLocaleTimeString(),
      actions,
      isLoading
    };
    setMessages(prev => [...prev, newMessage]);
  }, []);

  // Real-time event listener (SSE)
  React.useEffect(() => {
    let eventSource;
    if (window.EventSource) {
      eventSource = new window.EventSource(`${API.baseURL}/utils/events`);
      eventSource.onmessage = (event) => {
        if (event?.data) {
          addMessage(`Workspace update: ${event.data}`, false);
        }
      };
      eventSource.onerror = (err) => {
        console.warn('EventSource error:', err);
        eventSource.close();
      };
    }
    return () => {
      if (eventSource) eventSource.close();
    };
  }, [addMessage]);

  const createActionsForType = React.useCallback((actionType) => {
    switch (actionType) {
      case 'analyze_failures':
        return [{
          label: 'Analyze Failure Patterns',
          icon: <WarningIcon sx={{ color: 'warning.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=slices'),
        }];
      case 'set_gates':
        return [{
          label: 'Set Quality Gates',
          icon: <GateIcon sx={{ color: 'success.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=gate'),
        }];
      case 'optimize':
        return [{
          label: 'Optimize Performance',
          icon: <TrendingUpIcon sx={{ color: 'info.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=slices'),
        }];
      case 'intermediate':
        return [
          {
            label: 'Analyze Test Patterns',
            icon: <AnalyzeIcon sx={{ color: 'primary.main' }} />,
            primary: true,
            onClick: () => navigate('/workspace?tool=slices'),
          },
          {
            label: 'Compare Prompt Versions',
            icon: <LaunchIcon sx={{ color: 'info.main' }} />,
            onClick: () => navigate('/workspace?tool=diff'),
          },
        ];
      case 'run_tests':
        return [{
          label: 'Run More Tests',
          icon: <RunIcon sx={{ color: 'primary.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=live'),
        }];
      case 'ready_test':
        return [{
          label: 'Run First Test',
          icon: <RunIcon sx={{ color: 'success.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=live'),
        }];
      case 'create_suite':
        return [{
          label: 'Create Test Suite',
          icon: <AddIcon sx={{ color: 'warning.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=live'),
        }];
      case 'welcome':
        return [{
          label: 'Create First Prompt',
          icon: <AddIcon sx={{ color: 'primary.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=gate'),
        }];
      case 'analyze_runs':
        return [{
          label: 'View Analysis',
          icon: <AnalyzeIcon sx={{ color: 'primary.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=slices'),
        }];
      case 'start_test':
        return [{
          label: 'Start Test Run',
          icon: <RunIcon sx={{ color: 'success.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=live'),
        }];
      case 'create_prompt':
        return [{
          label: 'Create Prompt',
          icon: <AddIcon sx={{ color: 'primary.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=gate'),
        }];
      case 'create_options':
        return [
          {
            label: 'Create Prompt',
            icon: <AddIcon sx={{ color: 'primary.main' }} />,
            onClick: () => navigate('/workspace?tool=gate'),
          },
          {
            label: 'Create Test Suite',
            icon: <AddIcon sx={{ color: 'info.main' }} />,
            onClick: () => navigate('/workspace?tool=live'),
          },
        ];
      case 'compare':
        return [{
          label: 'Compare Versions',
          icon: <LaunchIcon sx={{ color: 'info.main' }} />,
          primary: true,
          onClick: () => navigate('/workspace?tool=diff'),
        }];
      case 'dashboard':
        return [{
          label: 'View Dashboard',
          icon: <AnalyzeIcon sx={{ color: 'primary.main' }} />,
          onClick: () => navigate('/workspace?tool=flow'),
        }];
      case 'help_actions':
        return [
          {
            label: 'Analyze Results',
            icon: <AnalyzeIcon sx={{ color: 'primary.main' }} />,
            onClick: () => navigate('/workspace?tool=slices'),
          },
          {
            label: 'Run Tests',
            icon: <RunIcon sx={{ color: 'success.main' }} />,
            onClick: () => navigate('/workspace?tool=live'),
          },
        ];
      case 'push_github':
        return [{
          label: 'Push to GitHub',
          icon: <LaunchIcon sx={{ color: 'info.main' }} />,
          primary: true,
          onClick: async () => {
            addMessage('Pushing to GitHub...', false, [], true);
            try {
              const res = await API.post('/utils/git/push', { commit_message: 'AI commit' });
              addMessage(res.data.message || 'Push complete.', false);
            } catch (err) {
              addMessage('Git push failed: ' + (err?.response?.data?.detail || err.message), false);
            }
          }
        }];
      case 'export_data':
        return [
          {
            label: 'Export as JSON',
            icon: <LaunchIcon sx={{ color: 'primary.main' }} />,
            onClick: async () => {
              addMessage('Exporting workspace data (JSON)...', false, [], true);
              try {
                const res = await API.get('/utils/export?type=json', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'workspace_export.json');
                document.body.appendChild(link);
                link.click();
                link.remove();
                addMessage('Export complete. Download started.', false);
              } catch (err) {
                addMessage('Export failed: ' + (err?.response?.data?.detail || err.message), false);
              }
            }
          },
          {
            label: 'Export as CSV',
            icon: <LaunchIcon sx={{ color: 'success.main' }} />,
            onClick: async () => {
              addMessage('Exporting workspace data (CSV)...', false, [], true);
              try {
                const res = await API.get('/utils/export?type=csv', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'workspace_export.csv');
                document.body.appendChild(link);
                link.click();
                link.remove();
                addMessage('Export complete. Download started.', false);
              } catch (err) {
                addMessage('Export failed: ' + (err?.response?.data?.detail || err.message), false);
              }
            }
          }
        ];
      default:
        return [];
    }
  }, [navigate]);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize with intelligent welcome message - only once on mount
  const initializeIntelligentChat = React.useCallback(async () => {
    // Prevent multiple simultaneous initializations
    if (isInitializing) return;

    setIsInitializing(true);

    try {
      let prompts = [];
      let suites = [];
      let runs = [];
      let latestRun = null;

      if (summary?.updatedAt) {
        prompts = Array.from({ length: summary.promptCount || 0 });
        suites = Array.isArray(summary.suites) ? summary.suites : Array.from({ length: summary.suiteCount || 0 });
        runs = Array.isArray(summary.recentRuns) ? summary.recentRuns : Array.from({ length: summary.runCount || 0 });
        latestRun = summary.lastRun || (Array.isArray(summary.recentRuns) ? summary.recentRuns[0] : null);
      } else {
        const [promptPayload, suitePayload, runPayload] = await Promise.all([
          API.prompts.list().catch(() => []),
          API.proof.suites().catch(() => []),
          API.proof.runs().catch(() => []),
        ]);
        prompts = promptPayload;
        suites = suitePayload;
        runs = runPayload;
        latestRun = Array.isArray(runPayload) && runPayload.length > 0 ? runPayload[0] : null;
      }

      const insights = analyzeWorkspaceData(prompts, suites, runs, latestRun, []);
      const actions = createActionsForType(insights.actionType);

      const introMessage = `${insights.message}\n\nCurrent focus: ${assistantMeta.label}. ${assistantMeta.prompt}`;

      addMessage(introMessage, false, actions);

    } catch (error) {
      console.error('Failed to initialize intelligent chat:', error);
      const errorActions = [{
        label: 'Refresh Page',
        icon: <RefreshIcon sx={{ color: 'warning.main' }} />,
        primary: true,
        onClick: () => {
          console.log('Refreshing page');
          window.location.reload();
        }
      }];
      addMessage('Unable to analyze workspace data. Please refresh and try again.', false, errorActions);
    } finally {
      setIsInitializing(false);
    }
  }, [assistantMeta.label, assistantMeta.prompt, summary, isInitializing, addMessage, analyzeWorkspaceData, createActionsForType]);

  React.useEffect(() => {
    if (hasBootstrappedRef.current) {
      return;
    }
    hasBootstrappedRef.current = true;
    initializeIntelligentChat();
  }, [initializeIntelligentChat]);

  React.useEffect(() => {
    if (lastAnnouncedToolRef.current === normalizedActiveTool) {
      return;
    }
    addMessage(`Switched focus to ${assistantMeta.label}. Let me know how I can help.`, false);
    lastAnnouncedToolRef.current = normalizedActiveTool;
  }, [normalizedActiveTool, assistantMeta.label, addMessage]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    addMessage(userMessage, true);

    setIsTyping(true);

    // Analyze user intent and provide specific response
    const response = await generateContextualResponse(userMessage);

    setIsTyping(false);
    addMessage(response.message, false, response.actions);
  };

  const generateContextualResponse = async (input) => {
    const lowercaseInput = input.toLowerCase();

    // SIMPLIFIED: Provide helpful responses without making duplicate API calls
    // The specific tools (SliceCore, etc.) will handle their own data loading

    // Intent recognition and specific responses
    if (lowercaseInput.includes('analyze') || lowercaseInput.includes('analysis')) {
      return {
        message: `Opening Slice Analysis to examine your performance patterns.`,
        actions: createActionsForType('analyze_runs')
      };
    }

    if (lowercaseInput.includes('test') || lowercaseInput.includes('run')) {
      return {
        message: `Opening Live Trial to run tests.`,
        actions: createActionsForType('start_test')
      };
    }

    if (lowercaseInput.includes('create') || lowercaseInput.includes('new')) {
      if (lowercaseInput.includes('prompt')) {
        return {
          message: `Opening Prompt Library to create a new prompt.`,
          actions: createActionsForType('create_prompt')
        };
      } else {
        return {
          message: `What would you like to create?`,
          actions: createActionsForType('create_options')
        };
      }
    }

    if (lowercaseInput.includes('compare') || lowercaseInput.includes('diff')) {
      return {
        message: `Opening Diff Tool to compare prompt versions.`,
        actions: createActionsForType('compare')
      };
    }

    if (lowercaseInput.includes('status') || lowercaseInput.includes('progress')) {
      return {
        message: `Opening workspace dashboard to check status.`,
        actions: createActionsForType('dashboard')
      };
    }

    // Recognize push/export requests
    if (lowercaseInput.includes('push') && lowercaseInput.includes('github')) {
      return {
        message: 'Ready to push your workspace to GitHub.',
        actions: createActionsForType('push_github')
      };
    }
    if (lowercaseInput.includes('export')) {
      return {
        message: 'Export workspace data as JSON or CSV.',
        actions: createActionsForType('export_data')
      };
    }
    // Default helpful response
    return {
      message: `You're working in ${assistantMeta.label}. I can help with prompts, testing, and analysis. What would you like to do next?`,
      actions: createActionsForType('help_actions')
    };
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Panel
      title="AI Chat"
      icon={<AIIcon />}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      contentSx={{ display: 'flex', flexDirection: 'column', p: 0 }}
    >
      {/* Messages Area */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'transparent',
      }}>
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}40`,
            borderRadius: 2,
            p: 1.5,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" color="primary" label={assistantMeta.label} />
            {additionalActiveTools.map((toolId) => {
              const meta = TOOL_ASSISTANT_COPY[toolId] || TOOL_ASSISTANT_COPY.live;
              return <Chip key={toolId} size="small" variant="outlined" label={meta.label} />;
            })}
            {summary.lastRun && (
              <Chip
                size="small"
                color={summary.lastRun.status === 'completed' || summary.lastRun.status === 'success' ? 'success' : summary.lastRun.status === 'failed' ? 'error' : 'default'}
                label={`Last run: ${(summary.lastRun.status || summary.lastRun.state || 'pending').toString().replace(/_/g, ' ')}`}
                sx={{ textTransform: 'capitalize' }}
              />
            )}
            <Chip size="small" label={`Prompts: ${promptCount}`} />
            <Chip size="small" label={`Suites: ${suiteCount}`} />
            <Chip size="small" label={`Runs: ${runCount}`} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {assistantMeta.prompt}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>
          {messages.map((message) => (
            <Box key={message.id} sx={{ mb: 2, display: 'flex', flexDirection: message.isUser ? 'row-reverse' : 'row', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{
                bgcolor: message.isUser ? 'success.main' : 'primary.main',
                color: 'white',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                flexShrink: 0
              }}>
                {message.isUser ? 'U' : 'AI'}
              </Box>

              <Box sx={{
                maxWidth: '85%',
                bgcolor: message.isUser ? 'rgba(34, 136, 141, 0.29)' : 'rgba(216, 168, 246, 0.24)',
                borderRadius: 2,
                p: 1.5,
                border: '1px solid',
                borderColor: message.isUser ? 'rgba(255, 255, 255, 0.34)' : 'rgba(255, 255, 255, 0.34)'
              }}>
                {message.isLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {message.content}
                    </Typography>
                  </Stack>
                ) : (
                  <>
                    {/* Clean text rendering - strip markdown formatting */}
                    <Typography variant="body2" sx={{
                      lineHeight: 1.4,
                      fontSize: '0.85rem',
                      whiteSpace: 'pre-line'
                    }}>
                      {message.content}
                    </Typography>

                    {message.actions && message.actions.length > 0 && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                        {message.actions.map((action, index) => (
                          <Button
                            key={index}
                            variant={action.primary ? 'contained' : 'outlined'}
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('Button clicked:', action.label);
                              if (action.onClick) {
                                try {
                                  action.onClick();
                                } catch (error) {
                                  console.error('Error in button onClick:', error);
                                }
                              } else {
                                console.error('No onClick handler for:', action.label);
                              }
                            }}
                            startIcon={action.icon}
                            sx={{
                              fontSize: '0.75rem',
                              py: 0.4,
                              px: 1.2,
                              minWidth: 'auto',
                              borderRadius: 1,
                              cursor: 'pointer'
                            }}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </Stack>
                    )}
                  </>
                )}

                {!message.isLoading && message.timestamp && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {message.timestamp}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}

          {isTyping && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{
                bgcolor: 'secondary.main',
                color: 'white',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                flexShrink: 0
              }}>
                AI
              </Box>

              <Box sx={{
                bgcolor: 'grey.50',
                borderRadius: 2,
                p: 1.5,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  AI is thinking...
                </Typography>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>
      </Box>

      {/* Input Area */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box component="input"
            placeholder="Ask me anything..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isTyping}
            sx={{
              flex: 1,
              p: 1,
              border: '1px solid',
              bgcolor: 'transparent',
              borderColor: 'rgba(255, 255, 255, 0.34)',
              borderRadius: 1,
              fontSize: '0.85rem',
              '&:focus': {
                outline: 'none',
                borderColor: 'primary.main'
              }
            }}
          />
          <Box
            component="button"
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isTyping}
            sx={{
              px: 2,
              py: 1,
              bgcolor: 'primary.main',
              color: 'white',
              border: 'none',
              borderRadius: 1,
              cursor: inputMessage.trim() && !isTyping ? 'pointer' : 'not-allowed',
              fontSize: '0.85rem',
              '&:hover': {
                bgcolor: inputMessage.trim() && !isTyping ? 'primary.dark' : 'primary.main'
              }
            }}
          >
            Send
          </Box>
        </Box>
      </Box>
    </Panel>
  );
}
